import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { navigableDestinations } from './destinations';
import { ensureSession } from '@/stores/session.store';

/**
 * Registro de componente por destino implementado. Cada corte que entrega uma família de tela
 * adiciona sua entrada aqui — nunca uma rota sem componente real (ver destinations.ts).
 */
const VIEW_BY_DESTINATION: Record<string, () => Promise<{ default: unknown }>> = {
  inicio: () => import('@/views/InicioView.vue'),
  disponiveis: () => import('@/views/DisponiveisView.vue'),
  servicos: () => import('@/views/ServicosView.vue'),
  agenda: () => import('@/views/AgendaView.vue'),
  perfil: () => import('@/views/PerfilView.vue'),
  relatorios: () => import('@/views/RelatoriosView.vue'),
};

function buildRoutes(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = [];
  for (const destination of navigableDestinations) {
    const loadView = VIEW_BY_DESTINATION[destination.name];
    if (!loadView) {
      throw new Error(`Destino "${destination.name}" está marcado implemented:true mas não tem view registrada em router/index.ts.`);
    }
    routes.push({ path: destination.path, name: destination.name, component: loadView });
  }
  return routes;
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/inicio' },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true } },
    { path: '/auth/callback', name: 'auth-callback', component: () => import('@/views/AuthCallbackView.vue'), meta: { public: true } },
    // Indisponibilidade do BFF em reload frio (guard `unreachable`). `public` para não recursar
    // no próprio guard; `standalone` mantém a tela fora do shell, como login e callback.
    { path: '/indisponivel', name: 'indisponivel', component: () => import('@/views/IndisponivelView.vue'), meta: { public: true, standalone: true } },
    // Telas públicas v2.1 (corte 0) — moldura AuthLayout; `/senha/:mode` valida o modo
    // (recover|setup) na própria view e redireciona ao login quando inválido.
    { path: '/cadastro', name: 'cadastro', component: () => import('@/views/CadastroView.vue'), meta: { public: true } },
    { path: '/senha/:mode', name: 'senha', component: () => import('@/views/SenhaView.vue'), meta: { public: true, standalone: true } },
    { path: '/privacidade', name: 'privacidade', component: () => import('@/views/PrivacidadeView.vue'), meta: { public: true } },
    ...buildRoutes(),
    // Sub-rota de detalhe da oferta — não é destino de navegação (não entra em destinations.ts,
    // que só lista itens de menu).
    { path: '/oferta/:id', name: 'oferta-detalhe', component: () => import('@/views/OfertaDetalheView.vue') },
    // Fluxo de atendimento (Corte 2) — empilhado, com retorno; não são itens de menu.
    { path: '/chamado/:id', name: 'chamado-detalhe', component: () => import('@/views/AtendimentoView.vue') },
    { path: '/ordem/:id', name: 'ordem-servico', component: () => import('@/views/OrdemServicoView.vue') },
    { path: '/lancar', name: 'lancar-chamado', component: () => import('@/views/LancarChamadoView.vue') },
    // Corte 2.2 — etapas do fluxo v2.1 (empilhadas, com retorno; não são itens de menu).
    { path: '/revisao/:id', name: 'revisao-categoria', component: () => import('@/views/RevisaoView.vue') },
    { path: '/valor/:id', name: 'valor-confirmacao', component: () => import('@/views/ValorView.vue') },
    { path: '/avaliacao/:id', name: 'avaliacao-clinica', component: () => import('@/views/AvaliacaoView.vue') },
    { path: '/equipamentos/:id', name: 'equipamentos-clinica', component: () => import('@/views/EquipamentosView.vue') },
    { path: '/equipamento-novo/:id', name: 'equipamento-novo', component: () => import('@/views/EquipamentoFormView.vue') },
    // Jornadas com prazo (Corte 3) — navegação tipada da Agenda (auditId/checkupId/loanId).
    { path: '/auditoria/:id', name: 'auditoria-detalhe', component: () => import('@/views/VistoriaView.vue') },
    { path: '/checkup/:id', name: 'checkup-detalhe', component: () => import('@/views/CheckupView.vue') },
    { path: '/emprestimo/:id', name: 'emprestimo-detalhe', component: () => import('@/views/EmprestimoView.vue') },
    // Sub-telas do Perfil (Corte 4) — empilhadas; o Perfil é o destino de menu.
    { path: '/perfil/recebimento', name: 'perfil-recebimento', component: () => import('@/views/RecebimentoView.vue') },
    { path: '/perfil/conta', name: 'perfil-conta', component: () => import('@/views/ContaView.vue') },
    { path: '/perfil/senha', name: 'perfil-senha', component: () => import('@/views/PerfilSenhaView.vue') },
    { path: '/perfil/certificacoes', name: 'perfil-certificacoes', component: () => import('@/views/CertificacoesView.vue') },
    { path: '/perfil/avaliacoes', name: 'perfil-avaliacoes', component: () => import('@/views/AvaliacoesView.vue') },
    { path: '/perfil/especialidades', name: 'perfil-especialidades', component: () => import('@/views/EspecialidadesView.vue') },
    // Vitrine interna do Design System — não é destino de produto, não entra em `destinations.ts`
    // (fonte única de navegação). Alvo dos snapshots Playwright e da verificação visual manual.
    {
      path: '/ds',
      name: 'design-system',
      component: () => import('@/views/DesignSystemView.vue'),
      meta: { standalone: true },
    },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.public === true || to.meta.standalone === true) return true;
  const result = await ensureSession();
  // `unavailable` = blip do BFF COM sessão em memória: as views já têm dados, a navegação segue
  // e o erro aparece no estado da própria view (AsyncState), sem deslogar ninguém.
  if (result.status === 'unauthenticated') return { name: 'login', query: { redirect: to.fullPath } };
  // Reload frio com o BFF fora do ar: nada em memória para montar o shell. Tela dedicada de
  // indisponibilidade, com retry que devolve o destino pretendido — nunca login (a sessão pode
  // estar válida) nem shell vazio.
  if (result.status === 'unreachable') return { name: 'indisponivel', query: { redirect: to.fullPath } };
  return true;
});
